import { motion } from 'framer-motion';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Swiper as SwiperType } from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import NewsPaper from '@/assets/ic_newsPaper.svg';
import { Card, Carousel } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { cn, fadeInOut } from '@/utils';

type Props = {
  children: React.ReactNode[] | React.ReactNode;
  indexHandler: (newActiveIndex: number) => void;
};

const NewsCarousel = ({ children, indexHandler }: Props) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const prevButtonRef = useRef<HTMLButtonElement | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div className="col-span-7 h-full p-12 pt-8 ">
      <p className="text-sm text-gdt-gray-3 font-semibold mb-4">NOVEDADES</p>
      <div className="w-full ">
        <Swiper
          autoplay={{
            delay: 12500,
            disableOnInteraction: false,
          }}
          centeredSlides={true}
          className="w-full h-full"
          loop={true}
          modules={[Autoplay, Pagination, Navigation]}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          pagination={{
            clickable: true,
          }}
          slidesPerView={1}
          spaceBetween={0}
          speed={900}
          onSlideChange={(swiper) => indexHandler(swiper.realIndex)}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            if (nextButtonRef.current && prevButtonRef.current) {
              swiper.navigation.init();
              swiper.navigation.update();
            }
          }}
        >
          {React.Children.map(children, (child) => {
            return <SwiperSlide className="w-full h-full">{child}</SwiperSlide>;
          })}
        </Swiper>
        <div className="flex justify-end items-center gap-8 mt-6">
          <button
            ref={prevButtonRef}
            className="w-10 h-10 flex items-center justify-center bg-gdt-blue-xl2 text-gdt-blue rounded-lg hover:shadow-[0px_0px_10px_rgba(173,216,230,0.5)]"
            onClick={() => swiperRef.current?.slidePrev()}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 19l-7-7 7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </button>
          <button
            ref={nextButtonRef}
            className="w-10 h-10 flex items-center justify-center bg-gdt-blue-xl2 text-gdt-blue rounded-lg hover:shadow-[0px_0px_10px_rgba(173,216,230,0.5)]"
            onClick={() => swiperRef.current?.slideNext()}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 5l7 7-7 7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export const NewsSection = memo(function NewsSection() {
  const { userInitialData } = useAuth();
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useIsDesktop();
  const [activeImage, setActiveImage] = useState(
    userInitialData?.sliderResponsive[0]?.urlImagen
  );
  const [activeUrl, setActiveUrl] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [activeImage]);

  const handleIndexChange = useCallback(
    (newActiveIndex: number) => {
      const newImage =
        userInitialData?.sliderResponsive[newActiveIndex]?.urlImagen;
      const newUrl =
        userInitialData?.sliderResponsive[newActiveIndex]?.linkWebResponsive;
      if (newImage) setActiveImage(newImage);
      setActiveUrl(newUrl || '');
    },
    [userInitialData?.sliderResponsive]
  );

  const cnCursor = activeUrl ? 'cursor-pointer' : 'cursor-default';

  return (
    <>
      {isDesktop && (
        <div className="bg-white h-[359px] w-full grid grid-cols-12 justify-center items-center text-black font-bold rounded-lg overflow-hidden">
          <div
            className={cn('col-span-5', 'h-full', 'overflow-hidden', cnCursor)}
            onClick={() => onClickHandler(activeUrl, navigate)}
          >
            <motion.div
              key={activeImage}
              animate={'show'}
              className="bg-cover w-full h-full"
              initial={false}
              variants={fadeInOut(0)}
            >
              <img
                alt="Imagen"
                className={`object-cover w-full h-full overflow-hidden transition-opacity duration-150 ${
                  loaded ? 'opacity-100' : 'opacity-0'
                }`}
                src={activeImage}
                onLoad={() => setLoaded(true)}
              />
            </motion.div>
          </div>
          <NewsCarousel indexHandler={handleIndexChange}>
            {userInitialData?.sliderResponsive.map((item) => {
              return (
                <div
                  key={item.titulo}
                  className="flex flex-col justify-start gap-4 w-full"
                >
                  <span
                    className={cn(
                      'text-lg',
                      'text-black',
                      'font-semibold',
                      'h-full',
                      cnCursor
                    )}
                    onClick={() => onClickHandler(activeUrl, navigate)}
                  >
                    {item.titulo}
                  </span>
                  <span
                    className={cn(
                      'text-base',
                      'text-gdt-gray-2',
                      'font-semibold',
                      cnCursor
                    )}
                    onClick={() => onClickHandler(activeUrl, navigate)}
                  >
                    {item.bajada}
                  </span>
                </div>
              );
            })}
          </NewsCarousel>
        </div>
      )}
      {isMobile && (
        <>
          <div className="flex flex-row items-center gap-4">
            <div className="p-3 bg-gdt-status-orange-light rounded-full">
              <img alt="ic NewsPaper" className="w-4" src={NewsPaper} />
            </div>
            <p className="mb-[6px] font-bold text-zinc-900 text-lg pl-1">
              Novedades
            </p>
          </div>
          <div className="w-full flex justify-center items-center">
            <Carousel IndexHandler={handleIndexChange}>
              {userInitialData?.sliderResponsive.map((item) => {
                return (
                  <Card
                    key={item.titulo}
                    className={cn(
                      'bg-white',
                      'p-0',
                      'text-center',
                      'min-w-[220px]',
                      'max-w-[440px]',
                      'h-[425px]',
                      'lg:max-w-[256px]',
                      'w-full',
                      'mr-4',
                      'rounded-tl-[12px]',
                      'rounded-tr-[12px]',
                      'lg:pb-8',
                      'pb-6',
                      cnCursor
                    )}
                    handleClick={() =>
                      onClickHandler(item.linkWebResponsive, navigate)
                    }
                  >
                    <picture>
                      <img
                        alt={item.titulo}
                        className="rounded-tl-[15px] rounded-tr-[15px] w-full"
                        src={item.urlImagen}
                      />
                    </picture>
                    <div className="flex flex-col items-start gap-2 mt-4 mx-4">
                      <span
                        className="text-lg text-black font-semibold text-start"
                        onClick={() => onClickHandler(activeUrl, navigate)}
                      >
                        {item.titulo}
                      </span>
                      <span className="text-base text-gdt-gray-2 font-semibold text-start">
                        {item.bajada}
                      </span>
                    </div>
                  </Card>
                );
              })}
            </Carousel>
          </div>
        </>
      )}
    </>
  );
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function onClickHandler(link: string | undefined, navigate: NavigateFunction) {
  // eslint-disable-next-line no-console
  console.debug('goto:' + link);
  if (link) {
    if (link.startsWith('http') || link.startsWith('../pdf/')) {
      window.open(link, '_blank');
    } else {
      navigate(link);
    }
  }
}

/**
 * Para debuggear que los links sean accesibles.
 * (ej: '/mi-equipo/futbol-5' debe responder aunque no tengas equipo,
 *  y te debe mandar al armado de F5)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LinkDebugger() {
  const navigate = useNavigate();
  return (
    <>
      {[
        '/partidos-recientes',
        '/mi-equipo/futbol-5',
        '/mis-medallas',
        '/mi-equipo/futbol-11',
        '/miniligas',
        '/mi-cuenta',
        '/ranking',
        '/torneo-de-amigos',
        '/torneo-de-escuelas/primarias',
        '/editar-cuenta',
        '/torneos/torneo-de-amigos-por-premios',
        '/resumen',
        '/copa-de-amigos',
      ].map((path, i) => {
        return (
          <div
            key={path + i}
            className=""
            onClick={() => {
              navigate(path); // Navigate to the specified path
            }}
          >
            {path}
          </div>
        );
      })}
    </>
  );
}
